import { useState } from 'react';
import PropTypes from 'prop-types';
import { styled, useTheme } from '@mui/material/styles';
import { Box, Typography, Grid, Tooltip, Menu, LinearProgress, Divider, Stack } from '@mui/material';
import { fCurrency, fPercent } from '../../../../utils/formatNumber';
import { calculatePpd } from '../../../../utils/calculatePpd';
import Iconify from '../../../../reusable-components/iconify';
import { BudgetBar } from './index';

SubcategoryBudget.propTypes = {
  subbudgets: PropTypes.array,
  cart: PropTypes.array,
  censusAmount: PropTypes.number,
  tab: PropTypes.number,
  daysLength: PropTypes.number,
  monthDaysLength: PropTypes.number,
  facId: PropTypes.any,
  includeUnapproved: PropTypes.bool,
  budgetDate: PropTypes.any,
  amountDaysInBudget: PropTypes.number,
  isDisplayingPPDValues: PropTypes.bool,
};

export default function SubcategoryBudget({
  subbudgets,
  cart,
  censusAmount,
  tab,
  daysLength,
  monthDaysLength,
  facId,
  includeUnapproved,
  budgetDate,
  amountDaysInBudget,
  isDisplayingPPDValues
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [budgetOpen, setBudgetOpen] = useState(null);
  const [open, setOpen] = useState(false);
  const [allExpanded, setAllExpanded] = useState(false);
  const [expanded, setExpanded] = useState(
    subbudgets.reduce((acc, subbudget, index) => {
      acc[index] = false;
      return acc;
    }, {})
  );
  const setAllSubcategoriesExpanded = () => {
    setExpanded((prev) => {
      const newState = {};
      Object.keys(prev).forEach((key) => {
        newState[key] = !allExpanded;
      });
      return newState;
    });
    // Toggle allExpanded state
    setAllExpanded(!allExpanded);
  };
  const handleChange = (index) => () => {
    setExpanded((prev) => ({ ...prev, [index]: !prev[index] }));
  };
  const sortedSubbudgets = [...subbudgets].sort((a, b) => b.weekBudgetAmount - a.weekBudgetAmount);
  const theme = useTheme();
  const determineColor = (value) => {
    if (value > 76) {
      return '#EB5757';
    }
    if (value > 50) {
      return '#F2C94C';
    }
    return theme.palette.info.main;
  };

  if (!subbudgets) return null;
  return (
    <>
      <Grid
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{
          my: 1,
        }}
      >
        <Grid item>
          {open && (
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-end"
              spacing={0.5}
              sx={{
                textDecoration: 'underline',
                '&:hover': {
                  cursor: 'pointer',
                },
              }}
              onClick={setAllSubcategoriesExpanded}
            >
              <Typography variant="subtitle2">{allExpanded ? 'Close' : 'Expand'} all subcategories</Typography>
              <Iconify icon={allExpanded ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={16} height={16} />
            </Grid>
          )}
        </Grid>
        <Grid item>
          <Grid
            container
            direction="row"
            justifyContent="flex-end"
            alignItems="flex-end"
            spacing={0.5}
            sx={{
              textDecoration: 'underline',
              '&:hover': {
                cursor: 'pointer',
              },
            }}
            onClick={() => setOpen(!open)}
          >
            <Typography variant="subtitle2">Subcategories</Typography>
            <Iconify icon={open ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={16} height={16} />
          </Grid>
        </Grid>
      </Grid>
      {open &&
        sortedSubbudgets.map((subbudget, index) => {
          const {
            category,
            flatRate,
            ppdAmount,
            monthBudgetAmount,
            monthSpent,
            weekBudgetAmount,
            weekSpent,
            calendarWeekBudgetAmount,
            calendarWeekSpent,
            agoraSubcategoryId,
            agoraCategoryId,
            daysInQuarter,
            daysInYear,
            quarterSpent,
            yearSpent,
            yearBudgetAmount,
            quarterBudgetAmount,
          } = subbudget;
          const cartTotalAmount = cart
            ?.filter(
              (item) =>
                item?.removedFromBudget !== true &&
                (item.subcategory === category || item.agoraSubcategory === category || item.agoraSubcategory?.name === category)
            )
            .map((item) => {
              if (Array.isArray(item?.budgetSourceData) && item.budgetSourceData.length > 0) {
                return item.budgetSourceData.reduce((sum, source, index) => {
                  const price = item.budgetPrice[index] || 0;
                  const quantity = item.budgetQty[index] || 0;
                  const tax = item.budgetTax[index] || 0;
                  const shipping = item.budgetShipping[index] || 0;
                  return sum + (price * quantity + tax + shipping);
                }, 0);
              }
              return item.price * item.quantity;
            })
            .reduce((a, b) => a + b, 0);

          const budget =
            tab === 1
              ? weekBudgetAmount
              : tab === 2
              ? quarterBudgetAmount
              : tab === 3
              ? yearBudgetAmount
            : tab === 4
              ? calendarWeekBudgetAmount
              : monthBudgetAmount || 0;
          const total =
            (tab === 1 ? weekSpent : tab === 2 ? quarterSpent : tab === 3 ? yearSpent : tab === 4 ? calendarWeekSpent : monthSpent) +
            (cartTotalAmount || 0);
          const history = tab === 1 ? weekSpent : tab === 2 ? quarterSpent : tab === 3 ? yearSpent : tab === 4 ? calendarWeekSpent : monthSpent || 0;
          const totalPpd = calculatePpd(total, censusAmount, amountDaysInBudget);

          return (
            <Box
              sx={{
                flexGrow: 1,
                position: 'relative',
                mb: 1,
                border: expanded[index] ? '1px solid #9baf87' : '1px solid #9baf877a',
                padding: expanded[index] ? 1 : 0.5,
              }}
              key={index}
            >
              <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  '&:hover': {
                    cursor: 'pointer',
                  },
                }}
                onClick={handleChange(index)}
              >
                <Typography variant="subtitle2" sx={{ textAlign: 'left' }}>
                  {category}
                </Typography>
                <Iconify icon={expanded[index] ? 'mdi:chevron-up' : 'mdi:chevron-down'} width={16} height={16} />
              </Grid>
              {expanded[index] ? (
                <>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Tooltip title="Budget">
                        <ExcelBox
                          sx={{
                            borderRadius: 0.5,
                            '&:hover': {
                              cursor: 'pointer',
                            },
                          }}
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setBudgetOpen(index);
                          }}
                        >
                          <Grid container direction="row" justifyContent="space-around" alignItems="center">
                            <Iconify icon="streamline-ultimate:money-bag-dollar" width={12} height={12} />

                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontSize: '10px',
                              }}
                            >
                              {fCurrency(budget)}
                            </Typography>
                            {!flatRate && (
                              <Iconify
                                icon={budgetOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'}
                                width={16}
                                height={16}
                              />
                            )}
                          </Grid>{' '}
                        </ExcelBox>
                      </Tooltip>
                    </Box>
                    <Menu
                      open={budgetOpen === index && !flatRate}
                      onClose={() => {
                        setAnchorEl(null);
                        setBudgetOpen(null);
                      }}
                      anchorEl={anchorEl}
                    >
                      <Box
                        sx={{
                          p: 1,
                          fontSize: '8px',
                        }}
                      >
                        <Typography fontSize="11px" color="secondary">{` Census = ${censusAmount}`}</Typography>
                        <Typography
                          fontSize="11px"
                          sx={{
                            color: (theme) => theme.palette.info.main,
                          }}
                        >{` PPD = ${fCurrency(ppdAmount)}`}</Typography>
                        <Typography fontSize="11px" color="secondary">{` ${
                          tab === 1
                            ? 'Week'
                            : tab === 2
                            ? 'Quarter'
                            : tab === 3
                            ? 'Year'
                            : tab === 4
                            ? 'Calendar Week'
                            : 'Month'
                        } = ${
                          tab === 1
                            ? daysLength
                            : tab === 2
                            ? daysInQuarter
                            : tab === 3
                            ? daysInYear
                            : tab === 4
                            ? 7
                            : monthDaysLength
                        } days`}</Typography>
                        <Divider sx={{ mt: 1, mb: 1 }} />
                        <Typography fontSize="11px" color="secondary" display="inline">
                          Census
                        </Typography>
                        <Typography fontSize="11px" display="inline">
                          {' '}
                          X{' '}
                        </Typography>
                        <Typography
                          fontSize="11px"
                          sx={{
                            color: (theme) => theme.palette.info.main,
                          }}
                          display="inline"
                        >
                          PPD
                        </Typography>
                        <Typography fontSize="11px" display="inline">
                          {' '}
                          X{' '}
                        </Typography>
                        <Typography fontSize="11px" color="secondary" display="inline">
                          {tab === 1
                            ? 'Week'
                            : tab === 2
                            ? 'Quarter'
                            : tab === 3
                            ? 'Year'
                            : tab === 4
                            ? 'Calendar Week'
                            : 'Month'}
                        </Typography>
                        <Typography fontSize="11px" display="inline">
                          {' '}
                          ={' '}
                          {fCurrency(
                            tab === 1
                              ? weekBudgetAmount
                              : tab === 2
                              ? quarterBudgetAmount
                              : tab === 3
                              ? yearBudgetAmount
                              : tab === 4
                              ? calendarWeekBudgetAmount
                              : monthBudgetAmount
                          )}
                        </Typography>
                      </Box>
                    </Menu>

                    <Box sx={{ flex: 1 }}>
                      <Tooltip title="Remaining">
                        <ExcelBox sx={{ borderRadius: 0.5 }}>
                          <Grid container direction="row" justifyContent="space-around" alignItems="flex-top">
                            <Iconify icon="fa6-solid:money-check-dollar" width={13} height={13} />
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontSize: '10px',
                              }}
                              color={budget - total < 0 ? 'error' : 'default'}
                            >
                              {fCurrency(budget - total)}
                            </Typography>
                          </Grid>
                        </ExcelBox>
                      </Tooltip>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <ExcelBox sx={{ borderRadius: 0.5 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontSize: '10px',
                          }}
                        >
                          PPD: {flatRate ? 'N/A' : fCurrency(ppdAmount)}
                        </Typography>
                      </ExcelBox>
                    </Box>
                  </Stack>
                  <BudgetBar
                    budget={budget}
                    total={total}
                    history={history}
                    catId={agoraCategoryId}
                    subcategoryId={agoraSubcategoryId}
                    facId={facId}
                    includeUnapproved={includeUnapproved}
                    budgetDate={budgetDate}
                    tab={tab}
                    censusAmount={censusAmount}
                    amountDaysInBudget={amountDaysInBudget}
                    isDisplayingPPDValues={isDisplayingPPDValues}
                  />
                </>
              ) : (
                <>
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                    }}
                    onClick={handleChange(index)}
                  >
                    <Typography
                      sx={{
                        color: total > budget ? 'white' : 'inherit',
                        flexShrink: 0,
                        fontSize: '12px'
                      }}
                    >
                      Total Spent:
                    </Typography>

                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: total > budget ? 'white' : 'inherit',
                        gap: 0.5,
                      }}
                    >
                      {isDisplayingPPDValues && totalPpd !== null ? <Typography sx={{fontSize: '12px' }} component="span">{`${fCurrency(totalPpd)} PPD /`}</Typography> : null}
                      {budget > 0 ? (
                        total - budget > 0 ? (
                          <>
                            <Typography sx={{fontSize: '12px' }} component="span">
                              {`${fCurrency(total)} (${fPercent(Math.floor((total / budget) * 100))})`}
                            </Typography>
                            <Tooltip title={`${fCurrency(total - budget)} over budget`} arrow>
                              <span style={{ cursor: 'pointer' }}>...</span>
                            </Tooltip>
                          </>
                        ) : total === 0 ? (
                          <Typography sx={{fontSize: '12px' }}  component="span">
                            $0.00 (0%)
                          </Typography>
                        ) : (
                          <Typography sx={{fontSize: '12px' }}  component="span">
                            {`${fCurrency(total)} (${fPercent(Math.floor((total / budget) * 100))})`}
                          </Typography>
                        )
                      ) : (
                        <Typography sx={{fontSize: '12px' }}  component="span">
                          No budget set
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <StyledLinearProgress
                    variant="determinate"
                    value={(total / budget) * 100 > 100 ? 100 : (total / budget) * 100 || 0}
                    sx={{
                      '& .MuiLinearProgress-bar': {
                        bgcolor: determineColor((total / budget) * 100),
                      },
                      borderBottom: `2px solid ${determineColor((total / budget) * 100)}`,
                    }}
                  />
                </>
              )}
            </Box>
          );
        })}
    </>
  );
}
const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  backgroundColor: '#fff',
  '& .MuiLinearProgress-barColorPrimary': {
    backgroundColor: theme.palette.primary.main,
  },
  '& .MuiLinearProgress-barColorInfo': {
    backgroundColor: theme.palette.info.main,
  },
  '& .MuiLinearProgress-barColorError': {
    backgroundColor: theme.palette.error.main,
  },
  '& .MuiLinearProgress-barColorWarning': {
    backgroundColor: theme.palette.warning.main,
  },
}));

const ExcelBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: 23,
  border: `1px solid ${theme.palette.secondary.light}`,
  boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.1)',
  borderRadius: 2,
}));
